import {BehaviorSubject, Observable, catchError, combineLatest, map, of, shareReplay, switchMap, throwError} from 'rxjs';
import {AppSettingsService} from 'src/app/services/app-settings.service';
import {HttpService} from 'src/app/services/http.service';
import {AbstractRestService} from 'src/app/shared/types/AbstractRestService';
import {BankAccountCounts} from '../models/bankAccountCounts.model';
import {Injectable} from '@angular/core';
import {BankAccountTableRowDto, ExternalClientBankAccountDto} from 'src/app/dashboard/bankAccounts/models/externalClientBankAccountDto.model';
import {HoldingAssign} from '../models/holdingAssign.model';
import {GpBankAccountsTableSearchOptions} from './components/gp-bank-account-display-table/gp-bank-accounts-table-search-options';
import {SearchOptionsResponse} from 'src/app/shared/models/SearchOptionsResponse.model';
import {BankAccountDto} from 'src/app/shared/models/cre-banking/plaid/bankAccount.model';

@Injectable({
  providedIn: 'root'
})
export class BankAccountsMsService extends AbstractRestService<ExternalClientBankAccountDto, any> {
  protected readonly baseEndpoint$ = this.appSettingsService.localEnv ?
    of('https://localhost:44360/api/BankAccount/') :
    of('BankAccount/');

  private _bankAccountCountsRefresh$ = new BehaviorSubject<void>(null);

  private _bankAccountsRefresh$ = new BehaviorSubject<void>(null)
  bankAccountsRefresh$ = this._bankAccountsRefresh$.asObservable();


  constructor(protected http: HttpService,
    private appSettingsService: AppSettingsService) {
    super(http);
  }

  bankAccountCountsRefresh() {
    this._bankAccountCountsRefresh$.next();
  }

  bankAccountRefresh(includeCount = false) {
    this._bankAccountsRefresh$.next();
    if (includeCount) {
      this.bankAccountCountsRefresh()
    }
  }

  getSearchOptionsToQuerystring(options: GpBankAccountsTableSearchOptions): string {
    let queryString = super.getSearchOptionsToQuerystring(options);

    queryString += `&includeClosed=${options.includeClosed}`;

    return queryString;
  }

  public getBankAccounts(options: GpBankAccountsTableSearchOptions): Observable<SearchOptionsResponse<BankAccountTableRowDto>> {
    var queryString = this.getSearchOptionsToQuerystring(options);
    return this.baseEndpoint$.pipe(
      switchMap(baseEndpoint => this.http.getTyped<SearchOptionsResponse<BankAccountTableRowDto>>(`${baseEndpoint}` + queryString)));
  }

  public getBankAccountsByHoldingId(holdingId: number): Observable<BankAccountTableRowDto[]> {
    var queryString = `?holdingId=${holdingId}`;
    return this.baseEndpoint$.pipe(
      switchMap(baseEndpoint => this.http.getTyped<SearchOptionsResponse<BankAccountTableRowDto>>(`${baseEndpoint}` + queryString)),
      map(response => response.rows));
  }

  public getBankAccountById(id: string): Observable<SearchOptionsResponse<BankAccountDto>> {
    return this.baseEndpoint$.pipe(
      switchMap(baseEndpoint => this.http.getTyped<SearchOptionsResponse<BankAccountDto>>(`${baseEndpoint}${id}`)));
  }

  public getBankAccountCounts(includeClosed: boolean): Observable<BankAccountCounts> {
    return this.baseEndpoint$.pipe(
      switchMap(baseUrl => this.http.getTyped<BankAccountCounts>(`${baseUrl}counts?includeClosed=${includeClosed}`)),
      catchError(err => {
        return of({
          gpClientBankAccountCount: 0,
          gpUnitBankAccountCount: 0,
          gpApplicationCount: 0,
          investorClientBankAccountCount: 0
        } as BankAccountCounts);
      })
    );
  }

  public getDisconnectedBankAccountCount(): Observable<number> {
    return this.baseEndpoint$.pipe(
      switchMap(baseUrl => this.http.getTyped<{count: number}>(`${baseUrl}disconnectedCount`)),
      map(result => result.count),
      catchError(err => {
        return of(0);
      })
    );
  }

  public getHoldingForAssignAccount(): Observable<HoldingAssign> {
    return this.baseEndpoint$.pipe(
      switchMap(baseUrl => this.http.getTyped<HoldingAssign>(`${baseUrl}holdings`)),
      catchError(err => throwError(() => err))
    );
  }
}
